import {
  Component,
  computed,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideCircleAlert, lucideTriangleAlert } from "@ng-icons/lucide";
import {
  BrnAlertDialogContent,
  HlmAlertDialog,
  HlmAlertDialogCancel,
  HlmAlertDialogContent,
  HlmButtonImports,
  HlmIconImports,
} from "@o2-fe/ui";
import {
  CustomTemplateType,
  CustomTemplateValidationError,
} from "../../../../shared/interfaces/custom-template.interface";

/**
 * Dialog component that surfaces the structured list of errors produced when
 * a user-uploaded `.docx` template fails validation.
 *
 * Opened by `TenantConfigSheetComponent` when `CustomTemplateService.validateTemplate()`
 * rejects with a `CustomTemplateValidationFailure` — every underlying error
 * reported by docxtemplater is shown in a scrollable list (no arbitrary cap),
 * with the Italian message, the offending tag name (if any), and the
 * surrounding template context. A CTA at the bottom lets the user download
 * the pre-built example template as a reference.
 *
 * Uses the same Spartan `HlmAlertDialog` pattern as the rest of the project
 * (see `MissingTenantConfigDialogComponent`).
 */
@Component({
  selector: "app-template-validation-errors-dialog",
  standalone: true,
  imports: [
    NgIcon,
    HlmIconImports,
    HlmButtonImports,
    HlmAlertDialog,
    HlmAlertDialogContent,
    BrnAlertDialogContent,
    HlmAlertDialogCancel,
  ],
  providers: [provideIcons({ lucideCircleAlert, lucideTriangleAlert })],
  templateUrl: "./template-validation-errors-dialog.component.html",
})
export class TemplateValidationErrorsDialogComponent {
  /** ViewChild for the underlying Spartan alert dialog. */
  protected readonly dialogRef =
    viewChild.required<HlmAlertDialog>(HlmAlertDialog);

  /** Current list of errors to display (set by the caller via `open()`). */
  protected readonly errors = signal<CustomTemplateValidationError[]>([]);

  /**
   * Which template type triggered the failure — passed back to the parent
   * via `downloadExampleRequested` when the user clicks "Scarica esempio",
   * so the parent knows which example file to download.
   */
  protected readonly templateType = signal<CustomTemplateType | null>(null);

  /**
   * Italian label matching the current template type, shown in the dialog
   * title to give the user immediate context ("Modello preventivo" vs
   * "Modello contratto").
   */
  protected readonly templateTypeLabel = computed(() => {
    const type = this.templateType();
    if (type === "quote") return "preventivo";
    if (type === "contract") return "contratto";
    return "";
  });

  /** Italian pluralization for the error count subheader. */
  protected readonly errorCountLabel = computed(() => {
    const count = this.errors().length;
    return count === 1 ? "1 problema" : `${count} problemi`;
  });

  /**
   * Emitted when the user clicks the "Scarica esempio" CTA inside the dialog.
   * Parent component handles the actual download via a DOM anchor.
   */
  readonly downloadExampleRequested = output<CustomTemplateType>();

  /**
   * Opens the dialog with the given errors and template type.
   * Called by the parent component after `validateTemplate()` rejects.
   *
   * @param errors - Structured validation errors from the service.
   * @param templateType - Whether the failing template was quote or contract.
   */
  open(
    errors: CustomTemplateValidationError[],
    templateType: CustomTemplateType,
  ): void {
    this.errors.set(errors);
    this.templateType.set(templateType);
    this.dialogRef().open();
  }

  /** Closes the dialog without taking any action. */
  protected onClose(): void {
    this.dialogRef().close();
  }

  /**
   * Emits a request to the parent component to download the example template
   * for the current template type, then closes the dialog.
   */
  protected onDownloadExample(): void {
    const type = this.templateType();
    if (type) {
      this.downloadExampleRequested.emit(type);
    }
    this.dialogRef().close();
  }
}
